import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';

import { getData } from 'services/api';
import { schema, defaultValues, convertResToForm } from './utils';
import { useResponsiveScreen } from 'hooks/useWindowSize';
import { MainHeader } from 'features/common/components/MainHeader';
import { MainLayout } from 'layouts/MainLayout';
import { FlexContainer } from 'features/common/components/FlexContainer';
import { CampaignForm } from './components/CampaignForm';
import { EmailPreview } from './components/EmailPreview';
import { CapacityModal } from './components/CapacityModal';
import { StickyBtnContainer } from './components/StickyBtnContainer';
import { CampaignBody, CampaignId, FormFields } from './types';
import { useCampaignMutatation } from './hooks/useCampaignMutation';
import { DesktopOnlyMessage } from '../components/DesktopOnlyMessage';

import { Subtitle, FormContainer } from './ManageEmailCampaign.styles';

export const ManageEmailCampaign = ({ isPunchup = false }: { isPunchup?: boolean }) => {
	const { campaignId } = useParams<CampaignId>();
	const { t } = useTranslation();
	// useResponsiveScreen() returns `width > 844` — i.e. a desktop-width screen, despite past
	// callers naming it `isMobile`. Marketing Campaigns is desktop-only (TIC-332).
	const isDesktop = useResponsiveScreen();
	const [isOverCapacity, setIsOverCapacity] = useState(false);

	const { data: campaign } = useQuery({
		queryKey: ['getCampaignFields', campaignId],
		queryFn: (): Promise<CampaignBody> => getData(`/marketing/email-campaigns/${campaignId}`),
		enabled: !!campaignId,
	});

	const formMethods = useForm<FormFields>({
		defaultValues: defaultValues(t),
		resolver: yupResolver(schema),
		values: convertResToForm(t, campaign),
	});

	const { partnerInfo, checkCapacity, handleClick, totalEmails } = useCampaignMutatation({
		campaignId,
		formMethods,
		setIsOverCapacity,
	});

	// TIC-330: pressing Enter in a single-line field triggers the browser's implicit form
	// submit, which sends the campaign without the Send/Draft button's onClick having set the
	// intended `status`. Sending a campaign must be an explicit button click — never Enter — so
	// we swallow Enter unless it originates from a multiline surface: a native <textarea> or a
	// contentEditable rich-text editor (the campaign body Quill editor), where Enter is a newline.
	const preventEnterSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
		if (event.key !== 'Enter') return;
		const target = event.target as HTMLElement;
		const isMultiline = target.tagName === 'TEXTAREA' || target.isContentEditable;
		if (!isMultiline) {
			event.preventDefault();
		}
	};

	return (
		<MainLayout containsStickyEl>
			{!isPunchup && (
				<MainHeader>
					<FlexContainer flexDirection="column">
						<MainHeader.Title element="h1">{t('marketing-email-campaigns')}</MainHeader.Title>
						<Subtitle isPunchup={isPunchup}>
							{campaign ? campaign.name : t('marketing-manageEmailCampaign-newCampaign')}
						</Subtitle>
					</FlexContainer>
				</MainHeader>
			)}
			{isDesktop ? (
				<FormContainer
					formMethods={formMethods}
					onSubmit={checkCapacity}
					onKeyDown={preventEnterSubmit}
					isPunchup={isPunchup}
				>
					<CampaignForm
						handleClick={handleClick}
						isLargeScreen={isDesktop}
						emailsSent={partnerInfo?.emailsSent}
						monthlyAllowance={partnerInfo?.emailsAllowance}
						isPunchup={isPunchup}
					/>
					<EmailPreview partnerImage={partnerInfo?.image || ''} isPunchup={isPunchup} />
					<StickyBtnContainer handleClick={handleClick} isLargeScreen={isDesktop} isPunchup={isPunchup} />
					{isOverCapacity && (
						<CapacityModal
							partnerInfo={partnerInfo}
							emailCount={totalEmails}
							toggleModal={setIsOverCapacity}
							handleClick={handleClick}
						/>
					)}
				</FormContainer>
			) : (
				<DesktopOnlyMessage isPunchup={isPunchup} />
			)}
		</MainLayout>
	);
};
